import React, { useState } from "react";
import { Container, Dropdown, Offcanvas } from "react-bootstrap";
import { Link, NavLink, useParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLeague } from "../hooks/useLeague";
import ChangePasswordModal from "../Auth/ChangePasswordModal";
import AdminNav from "./AdminNav";
import Footer from "../Common/Footer";
import MobileUserProfile from "../Common/MobileUserProfile";
import { usePageMetadata } from "../hooks/usePageMetadata";
import "../LeaguePublic/LeaguePublic.css";
import "./Admin.css";

export default function AdminLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const auth = useAuth();
  const { currentLeague, selectedSeasonId, setSelectedSeasonId } = useLeague();
  const { seasonId } = useParams();
  const location = useLocation();
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);
  const isAuthenticated = auth.isAuthenticated();

  // Set document title to league name
  usePageMetadata(currentLeague?.name);

  // Check if we're on a season management page
  const isSeasonPage =
    location.pathname.includes("/admin/seasons/") && seasonId;

  // Sync selectedSeasonId with URL param when on a season page
  React.useEffect(() => {
    if (isSeasonPage && seasonId) {
      const seasonIdNum = parseInt(seasonId, 10);
      if (selectedSeasonId !== seasonIdNum) {
        setSelectedSeasonId(seasonIdNum);
      }
    }
  }, [isSeasonPage, seasonId, selectedSeasonId, setSelectedSeasonId]);

  const handleCloseMobileMenu = () => setShowMobileMenu(false);
  const handleShowMobileMenu = () => setShowMobileMenu(true);

  // Determine the public site URL based on custom domain
  const getPublicSiteUrl = () => {
    if (!currentLeague) return "/";

    // If custom domain is set, link to that domain
    if (currentLeague.custom_domain) {
      return `https://${currentLeague.custom_domain}`;
    }

    // Otherwise, link to /leagues/:abbr on main domain
    return `/leagues/${currentLeague.abbr}`;
  };

  const publicSiteUrl = getPublicSiteUrl();

  return (
    <>
      <div className="admin-layout">
        <header className="lp-header">
          <div className="lp-header-top">
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              {currentLeague ? (
                <Link to="/admin" className="lp-brand">
                  <span className="lp-brand-mark" aria-hidden="true">
                    <i className="bi bi-bar-chart-steps" />
                  </span>
                  <span className="lp-brand-name">{currentLeague.name}</span>
                </Link>
              ) : (
                <Link to="/" className="lp-brand">
                  <span className="lp-brand-mark" aria-hidden="true">
                    <i className="bi bi-bar-chart-steps" />
                  </span>
                  <span className="lp-brand-name">Podium</span>
                </Link>
              )}
            </div>

            <div className="lp-header-actions">
              {/* Desktop Actions */}
              <div className="d-none d-md-flex align-items-center gap-2">
                {currentLeague?.custom_domain ? (
                  <a
                    href={publicSiteUrl}
                    className="lp-btn-ghost"
                    rel="noopener noreferrer"
                  >
                    <i className="bi bi-box-arrow-up-right me-1" />
                    View public site
                  </a>
                ) : (
                  <Link to={publicSiteUrl} className="lp-btn-ghost">
                    <i className="bi bi-box-arrow-up-right me-1" />
                    View public site
                  </Link>
                )}

                {isAuthenticated && auth.user && (
                  <>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "var(--text-secondary)",
                        fontWeight: 500,
                      }}
                    >
                      {auth.user.name || auth.user.email}
                    </span>
                    <Dropdown align="end">
                      <Dropdown.Toggle
                        variant="light"
                        size="sm"
                        className="lp-profile-toggle"
                        id="profile-dropdown"
                      >
                        <i className="bi bi-person-circle" />
                      </Dropdown.Toggle>

                      <Dropdown.Menu>
                        <Dropdown.Item as={Link} to="/profile">
                          <i className="bi bi-person me-2" />
                          Profile
                        </Dropdown.Item>
                        <Dropdown.Item as={Link} to="/leagues">
                          <i className="bi bi-trophy me-2" />
                          My Leagues
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item
                          onClick={(e) => {
                            e.preventDefault();
                            setShowChangePassword(true);
                          }}
                        >
                          <i className="bi bi-shield-lock me-2" />
                          Change Password
                        </Dropdown.Item>
                        <Dropdown.Divider />
                        <Dropdown.Item as={Link} to="/logout">
                          <i className="bi bi-box-arrow-right me-2" />
                          Logout
                        </Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  </>
                )}
              </div>

              {/* Mobile Hamburger */}
              <button
                className="lp-hamburger d-md-none"
                onClick={handleShowMobileMenu}
                aria-label="Open menu"
              >
                <i className="bi bi-list" />
              </button>
            </div>
          </div>
        </header>

        <div className="admin-body">
          <AdminNav />
          <div className="admin-content-wrapper">
            <Container fluid className="admin-main-content">
              {children}
            </Container>
          </div>
        </div>

        <Footer variant="app" />
      </div>

      <ChangePasswordModal
        show={showChangePassword}
        onHide={() => setShowChangePassword(false)}
      />

      {/* Mobile Offcanvas Menu */}
      <Offcanvas
        show={showMobileMenu}
        onHide={handleCloseMobileMenu}
        placement="end"
        className="admin-offcanvas"
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title>
            {currentLeague ? (
              <span className="lp-league-name">{currentLeague.name}</span>
            ) : (
              <div className="lp-brand">
                <span className="lp-brand-mark" aria-hidden="true">
                  <i className="bi bi-bar-chart-steps" />
                </span>
                <span className="lp-brand-name">Podium</span>
              </div>
            )}
          </Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <nav className="admin-nav-mobile">
            <div className="admin-nav-section-title">Admin</div>
            <NavLink
              to="/admin"
              end
              className={({ isActive }) =>
                `admin-nav-mobile-link${isActive ? " active" : ""}`
              }
              onClick={handleCloseMobileMenu}
            >
              <i className="bi bi-speedometer2 me-2" />
              Dashboard
            </NavLink>
            <NavLink
              to="/admin/league-settings"
              className={({ isActive }) =>
                `admin-nav-mobile-link${isActive ? " active" : ""}`
              }
              onClick={handleCloseMobileMenu}
            >
              <i className="bi bi-gear me-2" />
              League Settings
            </NavLink>
            <NavLink
              to="/admin/seasons"
              className={({ isActive }) =>
                `admin-nav-mobile-link${isActive ? " active" : ""}`
              }
              onClick={handleCloseMobileMenu}
            >
              <i className="bi bi-calendar-range me-2" />
              Seasons
            </NavLink>
            <NavLink
              to="/admin/players"
              className={({ isActive }) =>
                `admin-nav-mobile-link${isActive ? " active" : ""}`
              }
              onClick={handleCloseMobileMenu}
            >
              <i className="bi bi-people me-2" />
              Players
            </NavLink>
            <NavLink
              to="/admin/users"
              className={({ isActive }) =>
                `admin-nav-mobile-link${isActive ? " active" : ""}`
              }
              onClick={handleCloseMobileMenu}
            >
              <i className="bi bi-person-badge me-2" />
              Users
            </NavLink>

            {isSeasonPage && (
              <>
                <div className="lp-nav-divider" />
                <div className="admin-nav-section-title">Season {seasonId}</div>
                <NavLink
                  to={`/admin/seasons/${seasonId}/overview`}
                  className={({ isActive }) =>
                    `admin-nav-mobile-link${isActive ? " active" : ""}`
                  }
                  onClick={handleCloseMobileMenu}
                >
                  <i className="bi bi-card-text me-2" />
                  Overview
                </NavLink>
                <NavLink
                  to={`/admin/seasons/${seasonId}/teams`}
                  className={({ isActive }) =>
                    `admin-nav-mobile-link${isActive ? " active" : ""}`
                  }
                  onClick={handleCloseMobileMenu}
                >
                  <i className="bi bi-people-fill me-2" />
                  Teams & Players
                </NavLink>
                <NavLink
                  to={`/admin/seasons/${seasonId}/schedules`}
                  className={({ isActive }) =>
                    `admin-nav-mobile-link${isActive ? " active" : ""}`
                  }
                  onClick={handleCloseMobileMenu}
                >
                  <i className="bi bi-calendar3 me-2" />
                  Schedule
                </NavLink>
                <NavLink
                  to={`/admin/seasons/${seasonId}/scores`}
                  className={({ isActive }) =>
                    `admin-nav-mobile-link${isActive ? " active" : ""}`
                  }
                  onClick={handleCloseMobileMenu}
                >
                  <i className="bi bi-trophy me-2" />
                  Scores & Stats
                </NavLink>
              </>
            )}

            <div className="lp-nav-divider" />

            {currentLeague?.custom_domain ? (
              <a
                href={publicSiteUrl}
                className="admin-nav-mobile-link admin-nav-mobile-link-primary"
                onClick={handleCloseMobileMenu}
                rel="noopener noreferrer"
              >
                <i className="bi bi-box-arrow-up-right me-2" />
                View public site
              </a>
            ) : (
              <Link
                to={publicSiteUrl}
                className="admin-nav-mobile-link admin-nav-mobile-link-primary"
                onClick={handleCloseMobileMenu}
              >
                <i className="bi bi-box-arrow-up-right me-2" />
                View public site
              </Link>
            )}

            <MobileUserProfile onLinkClick={handleCloseMobileMenu} />
          </nav>
        </Offcanvas.Body>
      </Offcanvas>
    </>
  );
}
